import React from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import { apartmentInfo, contactInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Location = () => {
  const { t, language } = useLanguage();

  const locationDescriptions = {
    en: [
      "The apartment is located in the heart of Sarajevo, on quiet Koševo Street, overlooking a beautiful park. Perfect location for exploring the city - all major attractions are within walking distance.",
      "Baščaršija and the Old Town are just a 15-minute walk away, and the Presidency building of BiH is only 2 minutes away. Restaurants, cafes, shops, and public transport are in the immediate vicinity.",
      "For those who want to explore nature nearby, the Trebević cable car is only a 20-minute walk away, and the beautiful Vrelo Bosne is just 20 minutes by car. Ideal for combining city and nature experiences."
    ],
    hr: [
      "Apartman se nalazi u samom centru Sarajeva, u mirnoj ulici Koševo, s pogledom na prekrasan park. Savršena lokacija za istraživanje grada - sve glavne atrakcije su na pješačkoj udaljenosti.",
      "Do Baščaršije i Starog grada stižete za samo 15 minuta hoda, a zgrada Predsjedništva BiH je udaljena samo 2 minute. U neposrednoj blizini nalaze se restorani, kafei, trgovine i javni prijevoz.",
      "Za one koji žele istražiti prirodu u okolici, Trebević žičara je udaljena samo 20 minuta hoda, a prekrasno Vrelo Bosne samo 20 minuta autom. Idealno za kombinaciju gradskog i prirodnog doživljaja."
    ]
  };

  const distances = language === 'en' ? [
    { place: 'BiH Presidency', time: '2 min' },
    { place: 'Baščaršija', time: '15 min' },
    { place: 'City Hall', time: '12 min' },
    { place: 'Trebević cable car', time: '20 min walk' },
  ] : [
    { place: 'Predsjedništvo BiH', time: '2 min' },
    { place: 'Baščaršija', time: '15 min' },
    { place: 'Vijećnica', time: '12 min' },
    { place: 'Trebević žičara', time: '20 min hoda' },
  ];

  return (
    <section id="lokacija" className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-amber-700 text-sm font-medium tracking-wider mb-2">{t.location.subtitle}</p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">
            {t.location.title}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map */}
          <div className="rounded-2xl overflow-hidden shadow-xl h-96 lg:h-auto">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2877.1376138989846!2d18.410598176531385!3d43.85607263949889!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4758c8c98b73b3b1%3A0x5ec4b3b3b3b3b3b3!2sKo%C5%A1evo%2021%2C%20Sarajevo%2071000!5e0!3m2!1shr!2sba!4v1702500000000"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={language === 'en' ? 'Apartment location' : 'Lokacija apartmana'}
            />
          </div>

          {/* Info */}
          <div>
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-amber-100 p-3 rounded-xl">
                  <MapPin className="text-amber-700" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{t.contact.address}</h3>
                  <p className="text-gray-600">{t.location.address}</p>
                </div>
              </div>

              {locationDescriptions[language].map((desc, index) => (
                <p key={index} className="text-gray-600 leading-relaxed mb-4">
                  {desc}
                </p>
              ))}

              <a
                href={apartmentInfo.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <ExternalLink size={18} />
                {t.location.directions}
              </a>
            </div>

            {/* Quick distances */}
            <div className="grid grid-cols-2 gap-4">
              {distances.map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-4 shadow-md">
                  <p className="text-amber-700 font-bold text-lg">{item.time}</p>
                  <p className="text-gray-600 text-sm">{item.place}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
